import { CollaborationStatus } from '@bp/bp-graphql-types';
import { AddIcon, Button, DuplicateIcon, ErrorState, Grid, GridRow } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { FieldArray, useFormikContext } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useTranslation } from 'react-i18next';
import { CollaborationCourseType, CourseCollaborationFormRow } from './CourseCollaborationFormRow';
import { CourseFormType } from './CourseForm';

const CourseCollaborationFormDocument = graphql(`
  fragment CourseCollaborationFormFields on Query {
    collaboratingOrganizations(uuid: $organizationUuid) {
      uuid
      __typename
      collaborationStatus
      name
      groupAdmins {
        uuid
        __typename
        firstName
        lastName
      }
      collaborationAdmins {
        uuid
        __typename
        firstName
        lastName
      }
    }
  }
`);

type CourseCollaborationFormProps = {
  onLoad: (isLoading: boolean) => void;
  query: FragmentType<typeof CourseCollaborationFormDocument> | undefined;
};

export const CourseCollaborationForm = ({ onLoad, query }: CourseCollaborationFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();

  const { setFieldValue, setFieldTouched, values } = useFormikContext<CourseFormType>();

  const fragment = getFragmentData(CourseCollaborationFormDocument, query);
  const collaboratingOrganizations = fragment?.collaboratingOrganizations;
  const permissions = usePermissionChecker();

  const possibleCollaborations: CollaborationCourseType[] =
    collaboratingOrganizations
      ?.filter(
        (co) =>
          co.collaborationStatus === CollaborationStatus.Collaboration &&
          co.uuid !== pimAuthClaims.getOrganizationUuid() &&
          co.collaborationAdmins.length > 0,
      )
      .map((c) => {
        return {
          uuid: c.uuid,
          name: c.name,
          collaborationStatus: c.collaborationStatus,
          groupAdmins: c.groupAdmins.map((g) => {
            return { uuid: g.uuid, displayName: `${g.lastName}, ${g.firstName}`.trim() };
          }),
          collaborationAdmins: c.collaborationAdmins.map((g) => {
            return { uuid: g.uuid, displayName: `${g.lastName}, ${g.firstName}`.trim() };
          }),
        };
      }) ?? [];

  const remainingCollaborations = possibleCollaborations.filter((pc) => !values.collaborationSchools.includes(pc.uuid));

  return !permissions?.canCreateCollaborationGroups() ? (
    <ErrorState title={t('courses.canNotCreateCollaborationCourse')} />
  ) : (
    <FieldArray name='collaborationSchools'>
      {(arrayHelpers) => {
        return (
          <Grid useFormGap>
            {values.collaborationSchools.map((schoolUuid, index) => (
              <CourseCollaborationFormRow
                key={schoolUuid}
                index={index}
                helper={arrayHelpers}
                possibleCollaborations={possibleCollaborations}
                onDelete={async () => {
                  arrayHelpers.remove(index);
                  const updatedCollaborationAdmins = { ...values.collaborationAdmins };
                  delete updatedCollaborationAdmins[schoolUuid];
                  await setFieldValue('collaborationAdmins', updatedCollaborationAdmins);
                }}
              />
            ))}

            <GridRow spacingBottom='none' spacingTop='none'>
              <Button
                className='mr-4'
                hierarchy='tertiary'
                icon={<AddIcon />}
                onClick={async () => {
                  onLoad(true);
                  arrayHelpers.push('new');
                  onLoad(false);
                }}
                disabled={
                  values.collaborationSchools.includes('new') ||
                  values.collaborationSchools.length === possibleCollaborations.length
                }
              >
                {t('collaborations.addCollaboration')}
              </Button>
              <Button
                hierarchy='tertiary'
                icon={<DuplicateIcon />}
                onClick={async () => {
                  onLoad(true);
                  if (values.collaborationSchools.includes('new')) {
                    const index = values.collaborationSchools.findIndex((c) => c === 'new');
                    arrayHelpers.remove(index);
                  }
                  for (const { uuid, groupAdmins } of remainingCollaborations) {
                    arrayHelpers.push(uuid);
                    await setFieldTouched('collaborationAdmins');
                    await setFieldValue(
                      `collaborationAdmins.${uuid}`,
                      groupAdmins.map((g) => {
                        return { uuid: g.uuid, displayName: g.displayName ?? '' };
                      }),
                    );
                  }
                  onLoad(false);
                }}
                disabled={values.collaborationSchools.length === possibleCollaborations.length}
              >
                {`${t('collaborations.addAllCollaborations')} (${remainingCollaborations.length})`}
              </Button>
            </GridRow>
          </Grid>
        );
      }}
    </FieldArray>
  );
};
